import { v4 as UUIDv4 } from 'uuid';
import clone from 'lodash/clone';
import { getImageOssPath, getRemoteImageSize } from '@shakkerai/util-image';
import { getVipState } from '@shakkerai/pro-store';
import { translate } from '@shakkerai/util-i18n';
import { getTempState, updateTask } from '@starflow/app/store';
import { MODEL_SD35M, presetCheckpoints } from '@shakkerai/pro-models';
import { SD3_MODELS_FAMILY, SDXL_MODELS_FAMILY } from '@shakkerai/pro-models';
import { MODEL_FLUX, MODEL_SD35 } from '@shakkerai/pro-models';
import { presetCheckpointsMap } from '@shakkerai/pro-models';
import { fallbackModelBaseParams } from '@shakkerai/pro-models';
import { MODEL_SD15, MODEL_SDXL } from '@shakkerai/pro-models';
import { baseTypeToNumber, fixLoraWeight } from '@starflow/app/modules/models/helpers';
import { facialReferenceImages, facialReferenceDefaultValues } from '@shakkerai/pro-constants';
import { samplerMethodOptions, GENERATOR_ITEM_GAP, XINGLIU_MODEL_BASE_TYPE } from '@shakkerai/pro-constants';
import { controlNetInitialValues, controlNetConfig, processorSubParams } from '@shakkerai/pro-constants';
import { presetNegativePrompts, FAKE_IMAGE_URL } from '@shakkerai/pro-constants';
import { copyOSSImage, MAX_UPLOAD_SIZE } from '@shakkerai/pro-utils';
import { compressRemoteImage, isOverseas } from '@shakkerai/pro-utils';
import { calcPreprocessorResolution, mapSD3Params, handleTaskUpdate, applyGenerateResult } from '../helpers/generate';
import { getCanvasCenter, insertElements } from '../../../helpers/canvas';
import { pollingTaskStatus } from '@shakkerai/services';
import { notifyError } from '@shakkerai/pro-components';
import {
  updateUnitResult,
  updateUnitData,
  findFlowNextNodeId,
  saveFlowWhenUpdate,
  applyOutputToNode,
} from '../../../helpers/flow';
import { generatingStatus } from '@starflow/app/constants/index';

const stripUrlToken = (url) => (url ? url.replace(/\?Token=[^&]+/, '') : '');
const idleStatus = { idle: true, success: true, error: true };

export const defaultGenerateParamsHandler = async ({ type, params, sourceElement, isTryClacMode = false }) => {
  let enabledControlNets = [];

  const isSimpleMode = getTempState('globalMode') === 'simple';
  const promptSettingsParams = getTempState('promptSettingsParams');

  if (!params) {
    return null;
  }

  let baseModel = params.checkpoint;
  let paramsBaseType = MODEL_SDXL;

  if (!baseModel && !params.presetBaseModelId && params.loraModels?.length < 1) {
    paramsBaseType = MODEL_SDXL;
  } else {
    paramsBaseType = baseTypeToNumber(baseModel?.baseType || params.baseType || MODEL_SDXL);
  }

  if (params.presetBaseModelId && presetCheckpointsMap[params.presetBaseModelId]) {
    baseModel = presetCheckpointsMap[params.presetBaseModelId].versionDetail;
    paramsBaseType = baseTypeToNumber(presetCheckpointsMap[params.presetBaseModelId].versionDetail.baseType);
  }

  const isSD3FamilyModel = SD3_MODELS_FAMILY[paramsBaseType];
  const isFluxModel = paramsBaseType === MODEL_FLUX;
  const isSD35Model = paramsBaseType === MODEL_SD35 || paramsBaseType === MODEL_SD35M;
  const isGenMode = !isTryClacMode; // 是生成模式
  const isStarFlowModel = baseModel?.baseType === XINGLIU_MODEL_BASE_TYPE; // 是否是星流大模型

  if (!isSD3FamilyModel || isFluxModel) {
    enabledControlNets =
      params.controlnets?.filter((item) => item.type && item.sourceImage?.url && item.type !== 'reference') || [];
  }

  const referenceOptions = params.imageReference || {};

  let isImg2Img = false;
  let referenceImage = referenceOptions;
  let tempReferenceImagePath = null;
  let withFaceCN = false;

  if (referenceImage?.url) {
    isImg2Img = true;
  }

  if (isGenMode && referenceImage && referenceImage.url) {
    const remoteSize = await getRemoteImageSize(referenceImage.url);

    if (remoteSize.width > MAX_UPLOAD_SIZE || remoteSize.height > MAX_UPLOAD_SIZE) {
      try {
        const compressUrl = await compressRemoteImage(referenceImage.url, MAX_UPLOAD_SIZE, MAX_UPLOAD_SIZE, true);
        referenceImage = {
          ...referenceImage,
          isTemp: true,
          width: MAX_UPLOAD_SIZE,
          height: MAX_UPLOAD_SIZE,
          url: compressUrl,
        };
      } catch (error) {
        console.log(error);
      }
    }
  }

  if (isImg2Img) {
    if (isGenMode) {
      if (referenceImage.isTemp) {
        tempReferenceImagePath = getImageOssPath(referenceImage.url);
      } else {
        tempReferenceImagePath = await copyOSSImage(
          getImageOssPath(referenceImage.url),
          true,
          isOverseas ? undefined : '3',
        )
          .then((res) => res.url)
          .then(getImageOssPath);
      }
    } else {
      // 预估算力消耗会走到这里，用一个固定的参图片路径就行
      tempReferenceImagePath = 'copy/sd-images/2daf83d29cec1eacbaa1545a110179a180612c93bff7d9f1f150e62001a87f5e.png';
    }
  }

  const isSXDLModel =
    !paramsBaseType || SDXL_MODELS_FAMILY[paramsBaseType] || SDXL_MODELS_FAMILY[baseTypeToNumber(baseModel?.baseType)];

  const baseParams = {
    adetailerEnable: 0,
    mode: 1,
    uuid: UUIDv4(),
    projectData: clone(params),
    vae: '',
    taskQueuePriority: getVipState('vipAccelerate') ? 2 : 1, // 1 普通队列 2加速队列
  };

  baseParams.projectData.mode = 1;
  baseParams.projectData.isSimpleMode = isSimpleMode;
  baseParams.projectData.generateType = 'normal';
  baseParams.projectData.baseType = paramsBaseType;

  if (baseParams.projectData.checkpoint?.remark) {
    baseParams.projectData.checkpoint = { ...baseParams.projectData.checkpoint, remark: '' };
  }

  if (Array.isArray(baseParams.projectData.loraModels)) {
    baseParams.projectData.loraModels = baseParams.projectData.loraModels.map((item) => {
      return { ...item, remark: '' };
    });
  }

  let prompt = params.prompt?.trim() || '';
  let negativePrompt = params.negativePrompt?.trim() || '';

  // 去除提示词中的引号、大于号、小于号
  prompt = prompt?.replace(/['"<>]/g, '') || '';
  negativePrompt = negativePrompt?.replace(/['"<>]/g, '') || '';

  baseParams.projectData.prompt = prompt;
  baseParams.projectData.negativePrompt = negativePrompt;

  // 核心生图参数，传给txt2img或者img2img的
  const generateParams = {
    width: params.width,
    height: params.height,
    prompt: prompt,
    negativePrompt: negativePrompt,
    samplingMethod: params.samplerMethod || '15',
    samplingStep: isSimpleMode ? 30 : params.samplingSteps || 20,
    // hiresOptions: { ...params.hiresOptions },
    imgCount: params.count,
    batchSize: 1,
    batchCount: 1,
    cfgScale: params.cfgScale,
    clipSkip: params.clipSkip,
    seed: -1,
    tiling: 0,
    seedExtra: 0,
    restoreFaces: 0,
    hiResFix: 0,
    extraNetwork: [],
  };

  // 颜色控制生图
  let colorControl = null;
  if (params?.colorControl?.colorPalette?.length > 0) {
    let colorStrength = (params.colorControl?.colorStrength || 0.65) * 0.8;
    // 向下取整
    colorStrength = Math.floor(colorStrength / 0.05) * 0.05;
    colorControl = {
      colorPalette: params.colorControl.colorPalette,
      colorStrength: colorStrength,
    };
    generateParams.colorControl = colorControl;
  }

  Object.assign(baseParams, {
    checkpointId: baseModel?.versionId,
    additionalNetwork: params.loraModels
      ?.filter((lora) => baseTypeToNumber(lora.baseType) === paramsBaseType)
      .map((lora) => ({
        modelId: lora?.versionId,
        weight: fixLoraWeight(Number(Number(lora.weight || 0.8).toFixed(2))),
        type: 0,
      })),
  });

  if (isSD3FamilyModel) {
    generateParams.checkPointName = baseParams.checkpointId;
  }

  // 低门槛模式的话，使用通用+崩手预设，否则使用用户选择的预设
  const pickedPresetNegativePrompts = isSimpleMode ? ['common', 'bad_hand'] : params.presetNegativePrompts;

  if (paramsBaseType === MODEL_SD15) {
    generateParams.negativePrompt = [generateParams.negativePrompt]
      .concat(pickedPresetNegativePrompts.map((item) => presetNegativePrompts[item].text))
      .filter((item) => item)
      .join(',');

    if (!isSD3FamilyModel) {
      baseParams.vae = params.vae && params.vae !== '-1' ? Number(params.vae) : '';
    }
  }

  generateParams.promptRecommend = false;

  // 提示词处理
  // const originalPrompt = generateParams.prompt;
  generateParams.prompt = generateParams.prompt?.replace(/^[,]+/, '') || '';
  generateParams.negativePrompt = generateParams.negativePrompt?.replace(/^[,]+/, '') || '';

  if (params.presetBaseModelId) {
    const baseTypeNgPrompt = fallbackModelBaseParams[paramsBaseType]?.ngPrompt;
    if (baseTypeNgPrompt) {
      generateParams.negativePrompt += ',' + baseTypeNgPrompt;
    }
    const presetModel = presetCheckpoints.find((preM) => preM.id === params.presetBaseModelId);
    params.presetBaseModel = [presetModel];
  }

  const loraNgPrompts = params.loraModels
    ?.filter((lora) => !!lora.ngPrompt)
    ?.map((lora) => lora.ngPrompt)
    ?.join(',');

  if (loraNgPrompts) {
    generateParams.negativePrompt += ',' + loraNgPrompts;
  }

  if (baseModel?.ngPrompt) {
    generateParams.negativePrompt += ',' + baseModel.ngPrompt;
  }

  const loraTriggerWords = params.loraModels
    ?.filter((lora) => !!lora.triggerWord)
    ?.map((lora) => lora.triggerWord)
    ?.join(',');

  if (loraTriggerWords) {
    generateParams.prompt = loraTriggerWords + ',' + generateParams.prompt;
  }

  if (baseModel?.triggerWord) {
    generateParams.prompt = baseModel.triggerWord + ',' + generateParams.prompt;
  }

  if (params.seedType === '0' || isSimpleMode || params?.presetBaseModelId === 'base_checkpoint') {
    // 简单模式下也强制使用随机种子
    generateParams.seed = -1;
  } else {
    generateParams.seed = params.seedNumber ? Number(params.seedNumber) : -1;
  }

  generateParams.width = Math.round(generateParams.width);
  generateParams.height = Math.round(generateParams.height);

  // 增加提示词修饰
  baseParams.projectData.promptRecommend = !!promptSettingsParams?.prompt_setting_enhance;

  let { width, height } = generateParams;

  baseParams.projectData.renderWidth = width;
  baseParams.projectData.renderHeight = height;

  if (!isSD3FamilyModel) {
    if (isSimpleMode) {
      let hiresScale = 1;
      let hiresEnabled = false;

      // 低门槛模式自动计算的高清修复暗策略
      if (width < 512 && height < 512) {
        if (isSXDLModel) {
          // 等比缩放至长边1024
          const scale = Math.max(width, height) / 1024;
          generateParams.width = Math.round(width / scale);
          generateParams.height = Math.round(height / scale);
        } else {
          // 等比缩放至长边768
          const scale = Math.max(width, height) / 768;
          generateParams.width = Math.round(width / scale);
          generateParams.height = Math.round(height / scale);
        }
      } else if (width >= 512 && width <= 768 && height >= 512 && height <= 768) {
        if (isSXDLModel) {
          // 等比缩放至长边1024
          const scale = Math.max(width, height) / 1024;
          generateParams.width = Math.round(width / scale);
          generateParams.height = Math.round(height / scale);
        }
      } else if ((width >= 512 && width <= 768 && height > 768) || (width > 768 && height >= 512 && height <= 768)) {
        if (!isSXDLModel) {
          // 等比缩放至长边768
          // 按缩放后的比例生图并开启hires至原始宽高
          const scale = Math.max(width, height) / 768;
          generateParams.width = Math.round(width / scale);
          generateParams.height = Math.round(height / scale);

          hiresEnabled = scale > 1;
          hiresScale = scale;
        } else if (Math.max(width, height) > 1536) {
          // 分辨率长边设置为1536，短边等比缩放
          // 按缩放后的比例生图并开启hires至原始宽高
          const scale = Math.max(width, height) / 1536;
          generateParams.width = Math.round(width / scale);
          generateParams.height = Math.round(height / scale);

          hiresEnabled = scale > 1;
          hiresScale = scale;
        }
      } else if (width > 768 && height > 768) {
        if (!isSXDLModel) {
          // 等比缩放至长边768
          // 按缩放后的比例生图并开启hires至原始宽高
          const scale = Math.max(width, height) / 768;
          generateParams.width = Math.round(width / scale);
          generateParams.height = Math.round(height / scale);

          hiresEnabled = scale > 1;
          hiresScale = scale;
        } else if (width * height > 1024 * 1024) {
          let ratio = Math.sqrt((width * height) / (1024 * 1024));

          let fixedWidth = Math.round(width / ratio);
          let fixedHeight = Math.round(height / ratio);

          let newFixedWidth = fixedWidth - (fixedWidth % 8);
          let newFixedHeight = fixedHeight - (fixedHeight % 8);

          // ratio需要再修正一下
          ratio *= fixedWidth / newFixedWidth;

          generateParams.width = newFixedWidth;
          generateParams.height = newFixedHeight;

          hiresEnabled = ratio > 1;
          hiresScale = ratio;
        }
      }

      if (hiresEnabled) {
        generateParams.hiResFix = 1;

        const shortterSide = Math.min(generateParams.width, generateParams.height);

        if (shortterSide < 128) {
          const upfixScale = 128 / shortterSide;
          hiresScale /= upfixScale;
          generateParams.width = Math.ceil(generateParams.width * upfixScale);
          generateParams.height = Math.ceil(generateParams.height * upfixScale);
        }

        let resizeWidth = Math.ceil(generateParams.width * hiresScale);
        let resizeHeight = Math.ceil(generateParams.height * hiresScale);

        resizeWidth = resizeWidth - (resizeWidth % 8);
        resizeHeight = resizeHeight - (resizeHeight % 8);

        generateParams.hiResFixInfo = {
          upscaler: 10,
          upscaleBy: hiresScale,
          resizeWidth: resizeWidth,
          resizeHeight: resizeHeight,
        };

        generateParams.hiresSteps = 20;
        generateParams[isImg2Img ? 'hiresDenoisingStrength' : 'denoisingStrength'] = 0.5;
      }
    } else {
      // 进阶模式使用用户选择的高清修复参数
      if (params.hiresOptions?.enabled) {
        generateParams.hiResFix = 1;

        generateParams.hiResFixInfo = {
          upscaler: Number(params.hiresOptions.upscaler || 10),
          upscaleBy: params.hiresOptions.scale || 1,
          resizeWidth: generateParams.width - (generateParams.width % 8),
          resizeHeight: generateParams.height - (generateParams.height % 8),
        };

        generateParams.width = params.hiresOptions.width; // Math.round(generateParams.width / params.hiresOptions.scale);
        generateParams.height = params.hiresOptions.height; //Math.round(generateParams.height / params.hiresOptions.scale);

        generateParams.hiresSteps = params.hiresOptions.steps;
        generateParams[isImg2Img ? 'hiresDenoisingStrength' : 'denoisingStrength'] = params.hiresOptions.strength;
      }
    }
  }

  // 增加controlnet参数
  if (enabledControlNets.length) {
    let index = 0;
    baseParams.controlNet = [];

    for (const item of enabledControlNets) {
      let tempSourceImagePath = item.sourceImageId;

      if (!tempSourceImagePath && isGenMode) {
        tempSourceImagePath = await copyOSSImage(
          getImageOssPath(item.sourceImage.url),
          true,
          isOverseas ? undefined : '3',
        )
          .then((res) => res.url)
          .then(getImageOssPath);
      }

      // 算力计算情况
      if (!tempReferenceImagePath && !isGenMode) {
        tempReferenceImagePath = FAKE_IMAGE_URL;
      }

      // 算力计算情况
      if (!tempSourceImagePath && !isGenMode) {
        tempSourceImagePath = FAKE_IMAGE_URL;
      }

      const cnDefaultConfig = controlNetConfig[item.type]?.[paramsBaseType];

      let model = item.model || cnDefaultConfig?.models?.[0];
      let preprocessor = item.preprocessor || cnDefaultConfig?.preprocessors?.[0] || 0;

      let sourceSize = item.sourceImage;

      if (!sourceSize?.width && item.width) {
        sourceSize = {
          width: item.width,
          height: item.height,
        };
      }

      const pixelPerfect = !sourceSize ? 0 : typeof item.pixelPerfect !== 'undefined' ? Number(item.pixelPerfect) : 1;
      const preprocessorResolution = pixelPerfect ? calcPreprocessorResolution(sourceSize, generateParams) : 0;

      const annotationParameters = processorSubParams[preprocessor]
        ? {
            [processorSubParams[preprocessor].name]: Object.fromEntries(
              processorSubParams[preprocessor].items.map((key) => {
                if (key === 'preprocessorResolution' && pixelPerfect) {
                  return [key, preprocessorResolution];
                }

                return [key, item[key] || controlNetInitialValues[key]];
              }),
            ),
          }
        : item.annotationParameters || {};

      if (item.type === 'face') {
        if (!item.sourceImage?.url) {
          continue;
        }

        let tempReferenceImagePath = null;
        let referenceImageUrl = null;
        let referenceImageSize = null;

        const { referenceType, faceType, bodyType, referenceImage } = item;

        if (referenceType === 'preset') {
          const key = `${bodyType}_${faceType}`;

          if (facialReferenceImages[key]) {
            referenceImageUrl = facialReferenceImages[key][isOverseas ? 'shkUrl' : 'picUrl'];
            referenceImageSize = facialReferenceImages[key];
          }
        } else if (referenceType === 'same') {
          tempReferenceImagePath = tempSourceImagePath;
          referenceImageSize = sourceSize;
        } else if (referenceType === 'custom') {
          referenceImageUrl = referenceImage.url;
          referenceImageSize = referenceImage;
        }

        if (!referenceImageUrl && !tempReferenceImagePath) {
          continue;
        }

        if (!tempReferenceImagePath && isGenMode) {
          tempReferenceImagePath = await copyOSSImage(
            getImageOssPath(referenceImageUrl),
            true,
            isOverseas ? undefined : '3',
          )
            .then((res) => res.url)
            .then(getImageOssPath);
        }

        // 算力计算情况
        if (!tempReferenceImagePath && !isGenMode) {
          tempReferenceImagePath = FAKE_IMAGE_URL;
        }

        if (!tempSourceImagePath && !isGenMode) {
          tempSourceImagePath = FAKE_IMAGE_URL;
        }

        withFaceCN = true;

        baseParams.controlNet.push(
          {
            allowPreview: 1,
            unitOrder: ++index,
            width: sourceSize.width,
            height: sourceSize.height,
            annotationParameters: { instantIdFaceEmbedding: { preprocessorResolution } },
            controlMode: 0,
            controlWeight: +(
              item['faceEmbeding.controlWeight'] || facialReferenceDefaultValues['faceEmbeding.controlWeight']
            ),
            resizeMode: +(item['faceEmbeding.resizeMode'] || facialReferenceDefaultValues['faceEmbeding.resizeMode']),
            startingControlStep: +(
              item['faceEmbeding.startingControlStep'] ||
              facialReferenceDefaultValues['faceEmbeding.startingControlStep']
            ),
            endingControlStep: +(
              item['faceEmbeding.endingControlStep'] || facialReferenceDefaultValues['faceEmbeding.endingControlStep']
            ),
            pixelPerfect: pixelPerfect,
            pixelErfect: pixelPerfect,
            preprocessor: 60,
            model: 1128823,
            sourceImageId: tempSourceImagePath,
          },
          {
            allowPreview: 1,
            unitOrder: ++index,
            annotationParameters: { instantIdFaceKeypoints: { preprocessorResolution } },
            controlMode: 0,
            controlWeight: +(
              item['faceKeypoints.controlWeight'] || facialReferenceDefaultValues['faceKeypoints.controlWeight']
            ),
            resizeMode: +(item['faceKeypoints.resizeMode'] || facialReferenceDefaultValues['faceKeypoints.resizeMode']),
            startingControlStep: +(
              item['faceKeypoints.startingControlStep'] ||
              facialReferenceDefaultValues['faceKeypoints.startingControlStep']
            ),
            endingControlStep: +(
              item['faceKeypoints.endingControlStep'] || facialReferenceDefaultValues['faceKeypoints.endingControlStep']
            ),
            pixelPerfect: pixelPerfect,
            pixelErfect: pixelPerfect,
            preprocessor: 59,
            model: 1128809,
            width: referenceImageSize.width,
            height: referenceImageSize.height,
            sourceImageId: tempReferenceImagePath,
          },
        );
        continue;
      }

      const cnOptions = {
        allowPreview: 1,
        unitOrder: ++index,
        annotationParameters: annotationParameters,
        controlWeight: item.controlWeight,
        controlMode: +item.controlMode,
        startingControlStep: +item.startingControlStep,
        endingControlStep: +item.endingControlStep,
        pixelPerfect: pixelPerfect,
        pixelErfect: pixelPerfect,
        resizeMode: typeof item.resizeMode !== 'undefined' ? Number(item.resizeMode) : 1,
        preprocessor: +preprocessor,
        model: model ? +model : 0,
        sourceImageId: tempSourceImagePath,
        imagePosition: item.imagePosition,
      };

      if (item.maskImageId) {
        cnOptions.maskImageId = item.maskImageId;
      }

      baseParams.controlNet.push(cnOptions);
    }
  }

  // 低门槛模式或者是SD3模型，不使用adetailer
  const enabledAddon = isSD3FamilyModel || isSimpleMode ? [] : params.addOns?.filter((item) => item.value !== null);

  // 处理addons
  if (enabledAddon?.length) {
    baseParams.adetailerEnable = 1;

    baseParams.adetailerList = enabledAddon.map((item) => ({
      adetailerModelVerId: item.value || 0,
      prompt: '',
      negativePrompt: '',
      detection: {
        threshold: 0.3,
        maskMinAreaRatio: 0,
        maskMaxAreaRatio: 1,
      },
      maskPreprocessing: {
        maskXOffset: 0,
        maskYOffset: 0,
        maskScaling: item.edge,
        maskMergeMode: item.mergeMode || 0,
      },
      inpainting: {
        maskBlur: 4,
        denoisingStrength: 0.4,
        onlyMasked: true,
        onlyMaskedPaddingPixels: 32,
        separateWH: false,
        separateSteps: true,
        adetailerSteps: 25,
        separateCFGScale: false,
        separateNoiseMultiplier: false,
        restoreFacesAfterADetailer: false,
      },
    }));
  }

  const MOD_UNIT = 8;
  const restWidth = generateParams.width % MOD_UNIT;
  const restHeight = generateParams.height % MOD_UNIT;

  if (restWidth !== 0) {
    generateParams.width = generateParams.width - restWidth + MOD_UNIT;
  }

  if (restHeight !== 0) {
    generateParams.height = generateParams.height - restHeight + MOD_UNIT;
  }

  if (withFaceCN && isSimpleMode) {
    generateParams.cfgScale = 2.5;
  }

  if (isImg2Img) {
    let strength = referenceOptions.strength || 0;

    if (referenceOptions.mode === 'creative') {
      strength = 0.25;
    } else if (referenceOptions.mode === 'similar') {
      strength = 0.5;
    }

    generateParams.imageMode = 0;
    generateParams.maskImageId = '';
    generateParams.denoisingStrength = 1 - strength;

    generateParams.resizeScale = 1;
    generateParams.resizeMode = referenceOptions?.resizeMode || 1;
    generateParams.resizeWidth = generateParams.width;
    generateParams.resizeHeight = generateParams.height;
    generateParams.sourceImageId = tempReferenceImagePath;

    if (isFluxModel || isSD35Model) {
      baseParams.generateType = 22;
      baseParams.coreParamsField = 'img2img';
      baseParams.img2img = mapSD3Params(generateParams);
      if (isStarFlowModel && colorControl) {
        baseParams.img2img.colorControl = colorControl;
      }
    } else if (isSD3FamilyModel) {
      baseParams.generateType = 18;
      baseParams.coreParamsField = 'img2imgV3';
      baseParams.img2imgV3 = mapSD3Params(generateParams);
    } else {
      baseParams.generateType = 2;
      baseParams.coreParamsField = 'img2img';
      baseParams.img2img = generateParams;
    }
  } else {
    if (isFluxModel || isSD35Model) {
      baseParams.generateType = 21;
      baseParams.coreParamsField = 'text2img';
      baseParams.text2img = mapSD3Params(generateParams);
      if (isStarFlowModel && colorControl) {
        baseParams.text2img.colorControl = colorControl;
      }
    } else if (isSD3FamilyModel) {
      baseParams.generateType = 17;
      baseParams.coreParamsField = 'text2imgV3';
      baseParams.text2imgV3 = mapSD3Params(generateParams);
    } else {
      baseParams.generateType = 1;
      baseParams.coreParamsField = 'text2img';
      baseParams.text2img = generateParams;
    }
  }

  delete baseParams.projectData.controlNet;

  baseParams.projectData.samplerMethodName = samplerMethodOptions.find(
    (item) => item.value === generateParams.samplingMethod,
  )?.label;

  if (generateParams.comfyParams) {
    baseParams.comfyParams = generateParams.comfyParams;
    baseParams.comfyParams.projectData = baseParams.projectData;
    baseParams.comfyParams.taskQueuePriority = baseParams.taskQueuePriority;
  }

  return baseParams;
};

export const defaultGenerateResumeHandler = async (taskData) => {
  if (!taskData || !taskData.taskId) return;

  const taskLocalId = taskData.id;

  if (taskData.hasSubTasks) {
    return new Promise((resolve) => {
      taskData.results.forEach((item, index) => {
        if (!item.taskId || !generatingStatus[item.status]) return;

        const subTaskLocalId = item.id;

        pollingTaskStatus(item.taskId, {
          subTaskLocalId,
          onUpdate: handleTaskUpdate(taskLocalId),
        })
          .then((result) => {
            let allTaskComplete = false;

            updateTask(taskLocalId, (taskData) => {
              const updateData = {
                results: taskData?.results?.map((item) => {
                  if (item.id !== subTaskLocalId) return item;
                  return Object.assign({}, item, { status: 'idle' });
                }),
              };

              allTaskComplete = updateData.results.every((item) => idleStatus[item.status]);

              if (allTaskComplete) {
                updateData.error = null;
                updateData.taskId = null;
                updateData.status = 'idle';
              }

              return updateData;
            });

            // 将生成结果插入到画布
            applyGenerateResult(taskLocalId, result.videos || result.images, {
              taskId: item.taskId,
              subTaskIndex: index,
              width: taskData.width,
              height: taskData.height,
              projectData: Object.assign({}, taskData.params, item.params?.projectData),
            });

            if (allTaskComplete) {
              resolve();
            }
          })
          .catch((error) => {
            let allTaskComplete = false;

            updateTask(taskLocalId, (taskData) => {
              const updateData = {
                results: taskData.results.map((item) => {
                  if (item.id !== subTaskLocalId) return item;
                  return Object.assign({}, item, { status: 'error', error: error.message || error.msg || error });
                }),
              };

              allTaskComplete = updateData.results.every((item) => idleStatus[item.status]);

              if (allTaskComplete) {
                updateData.error = null;
                updateData.taskId = null;
                updateData.status = 'idle';
              }

              return updateData;
            });

            if (allTaskComplete) {
              resolve();
            }

            notifyError(translate('generator_failed'), error.message || error.msg || error);
          });
      });
    });
  }

  try {
    const result = await pollingTaskStatus(taskData.taskId, {
      onUpdate: handleTaskUpdate(taskLocalId),
    });

    if (!result) {
      updateTask(taskLocalId, {
        taskId: null,
        status: 'idle',
        progress: 0,
      });
    } else {
      const generatedWidth = taskData.params.renderWidth || taskData.width;
      const generatedHeight = taskData.params.renderHeight || taskData.height;

      console.log(`[DEBUG] apply polling result`, taskLocalId);
      applyGenerateResult(taskLocalId, result.videos || result.images, {
        taskId: taskData.taskId,
        width: generatedWidth,
        height: generatedHeight,
        projectData: taskData.params,
      });
    }
  } catch (error) {
    updateTask(taskLocalId, { status: 'error', error: error.message || error.msg || error });
    notifyError(translate('generator_failed'), error.message || error.msg || error);
  }
};

export const defaultGenerateResultHandler = async (taskData, results, params) => {
  let x, y;

  const { width, height, taskId } = params;

  if (typeof taskData.x === 'number') {
    x = taskData.x;
    y = taskData.y;
  } else {
    const center = getCanvasCenter();
    x = center.x;
    y = center.y;
  }

  // 生成器状态重置
  if (!taskData.hasSubTasks) {
    updateTask(taskData.id, { status: 'idle', results: [] });
  }

  if (params.subTaskIndex >= 0) {
    x = x + width * params.subTaskIndex + GENERATOR_ITEM_GAP * params.subTaskIndex;
  }

  console.log(`[DEBUG] taskData`, taskData);

  const resultSizes = [];

  try {
    if (taskData.isFakeSize) {
      for (const index in results) {
        const remoteSize = await getRemoteImageSize(
          stripUrlToken(results[index].coverPath || results[index].previewPath),
        );

        resultSizes.push(remoteSize);
      }
    }
  } catch (error) {
    console.log(`[DEBUG] 获取真实尺寸失败`, error);
  }

  const fixedResults = results.map((item, index) => {
    const resultItem = {
      x,
      y,
      width,
      height,
      type: item.type || 'image',
      innerTaskId: taskId,
      innerImageId: item.id,
      unitId: taskData.unitId,
      originalTaskId: taskId,
      originalImageId: item.id,
      originalUnitId: taskData.unitId,
      data: { illegal: item.illegal },
      scaleX: item.scaleX ?? 1,
      scaleY: item.scaleY ?? 1,
    };

    if (item.type === 'video') {
      resultItem.coverUrl = stripUrlToken(item.coverPath);
      resultItem.videoUrl = stripUrlToken(item.previewPath);
    }

    resultItem.url = stripUrlToken(item.previewPath);
    resultItem.originalUrl = resultItem.url;
    resultItem.data.seed = item.imageInfo?.match?.(/Seed: (\d+),/)?.[1];

    if (taskData.isFakeSize) {
      resultItem.width = resultSizes[index]?.width || width;
      resultItem.height = resultSizes[index]?.height || height;
    }

    x = x + resultItem.width + GENERATOR_ITEM_GAP;

    return resultItem;
  });

  if (taskData.unitId) {
    updateUnitResult(taskData.unitId, (data) => ({
      ...data,
      url: fixedResults[0].url,
      items: fixedResults.concat(data?.items || []),
    }));
  }

  // 插入生成结果到生成器位置, 自动运行只展示最后一个节点的结果
  // 非自动运行且不是从flow中手动发起的generator任务, 直接插入
  if (!taskData.isAuto && !taskData.isFlowManualGenerator) {
    insertElements(fixedResults, {
      autoSelect: false,
      skipTracking: true,
    });
  }

  // 自动运行更新下个节点的sourceElement
  if (taskData.isAuto) {
    const nextNodeId = findFlowNextNodeId(taskData.flowId, taskData.unitId);
    if (nextNodeId && fixedResults.length > 0) {
      updateUnitData(nextNodeId, {
        sourceElement: fixedResults[0],
      });
    }
    applyOutputToNode(taskData.unitId, fixedResults[0]);
  }

  // 如果是流上的操作, 保存流
  if (taskData.flowId) {
    saveFlowWhenUpdate(taskData.flowId);
  }

  return { results: fixedResults };
};
